import axios, { AxiosHeaders, AxiosPromise, AxiosRequestConfig, AxiosRequestHeaders, Method } from 'axios';
import { appContext } from '../AppContext';
import { GetAccessToken } from '../auth/AuthService';
import { GetAppSettings, GoFormzServices } from '../AppSettings';

export type HttpMethod = Method;
export type HttpHeaders = AxiosRequestHeaders;

const cache = {};
const internalWebApimPath = 'internal/web';
const publicShareApimPath = 'publicshare';
const appsApimPath = 'apps';

function getAppsApimUrl(url) {
  //url is in form of /dropbox/instanceId/webhoook

  //getting the apim url. the url can be modified based on config overrides for ApimOverrides
  const apimUrl = getApimUrl(GoFormzServices.AppsApimService, appsApimPath, url);

  const urlSplit = url.split('/');
  if (urlSplit.length < 4) {
    return apimUrl;
  }

  const appName = urlSplit[1]; //i.e. dropbox

  const appSettings = GetAppSettings();
  if (!appSettings.Apps || !appSettings.Apps[appName]) {
    return apimUrl;
  }

  const appOverride = appSettings.Apps[appName];
  const path = urlSplit[3]; //i.e. webhook

  return safeConcatUrl(appOverride, path);
}

function getApimKubernetsIngressRoute(apimApi, url) {
  const appSettings = GetAppSettings();

  //first try to route to an app override if available
  const urlParts = url.split('/').filter((part) => !!part);

  //expected route structure
  //if internal web path: apps/{appName}/path
  //if apps path: {appName}/path
  const requiredLength = apimApi == internalWebApimPath ? 2 : 1;
  const appNameIndex = apimApi == internalWebApimPath ? 1 : 0;

  if (urlParts.length > requiredLength) {
    const appName = urlParts[appNameIndex];
    const appKey = appSettings.Apps ? Object.keys(appSettings.Apps).find((configAppName) => configAppName.toLowerCase() == appName.toLowerCase()) : null;

    if (appKey) {
      const appOverride = appSettings.Apps[appKey];
      return safeConcatUrl(appOverride, urlParts.slice(appNameIndex + 1).join('/'));
    }
  }

  //fallback to the kubernetes ingress override
  if (appSettings.ApimOverrides && appSettings.ApimOverrides[GoFormzServices.AppsApimService]) {
    return safeConcatUrl(appSettings.ApimOverrides[GoFormzServices.AppsApimService], url);
  }

  return safeConcatUrl(appSettings.APIMUrl, apimApi, url);
}

function getApimUrl(serviceName, apimApi, url) {
  const appSettings = GetAppSettings();

  if (serviceName === GoFormzServices.AppsApimService) {
    return getApimKubernetsIngressRoute(apimApi, url);
  }

  if (serviceName && appSettings.ApimOverrides && appSettings.ApimOverrides[serviceName]) {
    return safeConcatUrl(appSettings.ApimOverrides[serviceName], url);
  }

  return safeConcatUrl(appSettings.APIMUrl, apimApi, url);
}

function getPublicShareApimUrl(url) {
  const apimUrl = getApimUrl(GoFormzServices.PublicFormsService, publicShareApimPath, url);
  return apimUrl;
}

function getWebApimUrl(serviceName, url) {
  const apimUrl = getApimUrl(serviceName, internalWebApimPath, url);
  return apimUrl;
}

function getAuthToken() {
  return GetAccessToken();
}

function getAxiosApiOptions(
  method: HttpMethod = 'GET',
  body = null,
  cancellationToken = null,
  includeAuth = true,
  headers: HttpHeaders = null,
  responseType: 'json' | 'blob' = 'json'
): AxiosRequestConfig {
  // incoming headers should be able to override defaults
  const defaultHeaders = responseType === 'json' ? { Accept: 'application/json', 'Content-Type': 'application/json' } : {};
  const options: AxiosRequestConfig = {
    headers: { ...defaultHeaders, ...headers },
    responseType,
    method: method,
    data: responseType === 'json' && body ? JSON.stringify(body) : body,
    cancelToken: cancellationToken
  };

  if (includeAuth) {
    options.headers.Authorization = `Bearer ${getAuthToken()}`;
  }

  return options;
}

function getModifiedApimHeaders(serviceName: string, headers: Partial<HttpHeaders> = null): HttpHeaders {
  const modifiedHeaders = { ...headers };
  const appSettings = GetAppSettings();
  //checking to see if we need to add the SetBackendServiceUrl
  //this is used by the apim to override the forwarding url
  if (serviceName) {
    //for local development only
    if (appSettings.SetBackendServiceUrlHeaders && appSettings.SetBackendServiceUrlHeaders[serviceName]) {
      modifiedHeaders['SetBackendServiceUrl'] = appSettings.SetBackendServiceUrlHeaders[serviceName];
    }

    //override with whatever is in the routes config
    if (appSettings.Routes && appSettings.Routes[serviceName]) {
      modifiedHeaders['SetBackendServiceUrl'] = appSettings.Routes[serviceName];
    }
  }

  if (appSettings?.RoutingEnvironment && appSettings.RoutingEnvironment.toLowerCase() !== 'default') {
    const apimOverrides = appSettings.ApimOverrides || {};
    const serviceBusOverrides = Object.fromEntries(Object.keys(appSettings.ServiceBusOverrides || {}).map((key) => [key, '']));
    const appsOverrides = Object.fromEntries(Object.keys(appSettings.Apps || {}).map((key) => [key, '']));
    const setBackendServiceUrlHeaders = appSettings.SetBackendServiceUrlHeaders || {};

    //lab usage
    const routes = appSettings.Routes || {};

    //this object represents all the apim overrides but are set only for local request routing.
    //this allows for services behind the apim to still route messages to a local machine but not http requests
    const apimOverridesForMessagingOnly = { ...(apimOverrides || {}) };
    for (const key in apimOverridesForMessagingOnly) {
      if (apimOverridesForMessagingOnly.hasOwnProperty(key)) {
        apimOverridesForMessagingOnly[key] = '';
      }
    }

    //the following check looks at the apim overrides to see if this request is going to bypass the apim and go direct to a service.
    //if it is then send up all the overrides in the routes.
    //if it is NOT then we do not want to send up any of the apim overrides. this is because services behind the apim cant route
    //to local services so we do want to leave those out.
    const routesHeaderValue = apimOverrides[serviceName]
      ? { ...serviceBusOverrides, ...appsOverrides, ...apimOverrides, ...setBackendServiceUrlHeaders, ...routes }
      : { ...serviceBusOverrides, ...appsOverrides, ...apimOverridesForMessagingOnly, ...setBackendServiceUrlHeaders, ...routes };

    modifiedHeaders['X-Routing'] = JSON.stringify({
      routes: routesHeaderValue,
      originatingEnvironment: appSettings.RoutingEnvironment
    });
  }

  return modifiedHeaders as AxiosRequestHeaders;
}

export function safeConcatUrl(...urlParts) {
  const safeBase = urlParts[0].endsWith('/') ? urlParts[0].slice(0, -1) : urlParts[0];
  const safeUrl =
    safeBase +
    urlParts
      .slice(1)
      .map((path) => (path ? (path.startsWith('/') ? path : `/${path}`) : ''))
      .join('');
  return safeUrl;
}

export function apiCall<TData = any>(
  url: string,
  method: HttpMethod = 'GET',
  body = null,
  cancellationToken = null,
  headers: HttpHeaders = null,
  includeOptions = true,
  responseType: 'json' | 'blob' = 'json'
): AxiosPromise<TData> {
  const options = getAxiosApiOptions(method, body, cancellationToken, true, headers, responseType);
  return axios(url, includeOptions ? options : null);
}

export function apimCall<TData = ExplicitAny>(
  serviceName: string,
  url: string,
  method: HttpMethod = 'GET',
  body = null,
  cancellationToken = null,
  headers: Partial<HttpHeaders> = null,
  responseType: 'json' | 'blob' = 'json'
) {
  //getting the apim url. the url can be modified based on config overrides for ApimOverrides
  const apimUrl = getWebApimUrl(serviceName, url);

  //overriding the apim headers to allow for changing the backend service
  //this is used for labs
  const modifiedHeaders = getModifiedApimHeaders(serviceName, headers);

  return apiCall<TData>(apimUrl, method, body, cancellationToken, modifiedHeaders, true, responseType);
}

export function getApiUploadFileOptions(file, method = 'POST') {
  const formData = new FormData();
  formData.append('file1', file);

  const options = {
    body: formData,
    method,
    mode: 'cors',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  };

  return options;
}

export function getBlobUploadFileOptions(file, method = 'POST') {
  const options = {
    body: file,
    method,
    mode: 'cors',
    headers: {}
  };
  options.headers['x-ms-blob-type'] = 'BlockBlob';

  return options;
}

export async function apimCallWithCache<TData = any>(
  serviceName: string,
  url: string,
  method: HttpMethod = 'GET',
  body = null,
  headers?: Partial<HttpHeaders>,
  cacheKeyBase?: string,
  refreshCache = false
) {
  let cacheKey = cacheKeyBase || `url=${url}|method=${method}`;
  if (body) {
    cacheKey += `|body=${JSON.stringify(body)}`;
  }
  if (headers) {
    cacheKey += `|headers=${JSON.stringify(headers)}`;
  }
  if (!refreshCache && cache[cacheKey]) {
    return cache[cacheKey];
  }

  const response = await apimCall<TData>(serviceName, url, method, body, null, headers);
  cache[cacheKey] = response;
  return response;
}

export async function appsApimCall<TData = any>(url, method: HttpMethod = 'GET', body = null, headers?: HttpHeaders) {
  //overriding the apim headers to allow for changing the backend service
  //this is used for labs
  const modifiedHeaders = getModifiedApimHeaders(GoFormzServices.AppsApimService, headers);

  //get apim url or an override from the config
  const appsUrl = getAppsApimUrl(url);
  const response = await apiCall<TData>(appsUrl, method, body, null, modifiedHeaders);
  return response;
}

export async function appsWebApimCal<TData = any>(url, method: HttpMethod = 'GET', body = null, headers?: HttpHeaders) {
  //overriding the apim headers to allow for changing the backend service
  //this is used for labs
  const modifiedHeaders = getModifiedApimHeaders(GoFormzServices.AppsApimService, headers);

  //get apim url or an override from the config
  const appsUrl = getAppsApimUrl(url);
  const response = await apiCall<TData>(appsUrl, method, body, null, modifiedHeaders);
  return response;
}

export async function appsApimCallWithCache<TData = any>(url, method: HttpMethod = 'GET', body = null, headers?: HttpHeaders) {
  let cacheKey = `url=${url}|method=${method}`;
  if (body) {
    cacheKey += `|body=${JSON.stringify(body)}`;
  }
  if (headers) {
    cacheKey += `|headers=${JSON.stringify(headers)}`;
  }
  if (cache[cacheKey]) {
    return cache[cacheKey];
  }

  const response = await appsApimCall<TData>(url, method, body, headers);
  cache[cacheKey] = response;
  return response;
}

export function publicShareApimCall<TData = any>(
  url: string,
  method: HttpMethod = 'GET',
  body = null,
  cancellationToken = null,
  sharedAccessToken: string = null,
  saveId: string = null
): AxiosPromise<TData> {
  const fetchBody = body ? JSON.stringify(body) : '';
  const options: AxiosRequestConfig = {
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    method: method,
    data: fetchBody,
    cancelToken: cancellationToken
  };

  const state = appContext.store.getState();

  if (!sharedAccessToken) {
    sharedAccessToken = state.formeditor.sharedAccessToken;
  }
  if (sharedAccessToken) {
    options.headers[sharedAccessToken.length > 20 ? 'X-Policy' : 'X-Policy-Token'] = sharedAccessToken;
  }
  if (saveId) {
    options.headers['X-Save-Id'] = saveId;
  }

  //overriding the apim headers to allow for changing the backend service
  //this is used for labs
  options.headers = getModifiedApimHeaders(GoFormzServices.PublicFormsService, options.headers as AxiosHeaders);

  //getting the apim url. the url can be modified based on config overrides for ApimOverrides
  const apimUrl = getPublicShareApimUrl(url);
  return axios(apimUrl, options);
}
